import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Breadcrums from "../../../common/breadcrumbs";
import { STATUS, SWAL_SETTINGS, THEME_COLORS, VERIFICATION_STATUS } from "../../../../../utils/Constants";
import {
  TrimText,
  formateDateWithMonth,
  handleServerValidations,
  hasP,
} from "../../../../../utils/commonfunction";
import * as players from "../../../services/players.service";
import { ExportUser, ExportUserPDF } from "../../../services/usermgmt.services";
import * as clubAdmin from "../../../services/clubAdmin.service";
import { useTranslation } from "react-i18next";
import { CustomProvider, DateRangePicker, Placeholder, Tooltip, Whisper } from "rsuite";
import { useQuery } from "react-query";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import NoRowsOverlay from "../../../common/NoRowsOverlay";
import moment from "moment";

ModuleRegistry.registerModules([AllCommunityModule]);

const PlayersList = () => {
  const params = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const gridRef = useRef();

  const [searchData, setSearchData] = useState("");
  const [statsupdate, setStatusUpdate] = useState("false");
  const [pageIndex] = useState(Number(params.pgno) - 1 || 0);
  const [perPage, setPerPage] = useState(10);
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: t("Player"), url: "" },
  ];

  useEffect(() => {
    clubAdmin.List({ limit: 1000, filters: { isProfileVerified: "verified", isBlocked: false } }).then(res => {
      if (res.success) {
        const activeClubs = (res.data.list || []).filter(
          c => !c.isDeleted && 
               c.isDeleted !== "true" && 
               !c.deletedAt && 
               c.status !== "DELETED" && 
               (c.isProfileVerified === "verified" || c.isProfileVerified === true || c.isProfileVerified === "true") && 
               !c.isBlocked
        );
        setClubs(activeClubs);
      }
    });
  }, []);

  // ================= FETCH ALL DATA =================
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["players-list", selectedClub],
    queryFn: () => players.List({
      limit: 1000,
      lastKey: null,
      filters: { clubAdminId: selectedClub },
      sorting: {}
    }),
    refetchOnWindowFocus: false,
    retry: false,
    onError: (error) => {
      Swal.fire({
        icon: "error",
        text: handleServerValidations(error),
        ...SWAL_SETTINGS,
      });
    },
  });

  // ================= ACTIONS =================
  const viewfunction = (row) => {
    navigate(`/admin/user-management/players/view/${row.originalId}`);
  };

  const Editfunction = (row) => {
    navigate(`/admin/user-management/players/${params.pgno}/edit/${row.originalId}`);
  };

  const downloadPDF = async (row) => {
    try {
      const response = await ExportUserPDF(row.originalId);
      const blob = response instanceof Blob ? response : response?.data;
      if (blob instanceof Blob) {
        let fileName = "";
        const contentDisposition = response?.headers?.["content-disposition"];
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^";]+)"?/);
          if (match && match[1]) {
            fileName = match[1];
          }
        }
        if (!fileName) {
          const surnameOrName = row.surname || row.lastName || row.username || row.firstName || row.name || row.originalId;
          const safeSurname = String(surnameOrName).trim().replace(/[^a-zA-Z0-9_-]/g, "_");
          fileName = `User_Report_${safeSurname}.pdf`;
        }

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        Swal.fire({ icon: "error", text: "Failed to download PDF. Please try again.", ...SWAL_SETTINGS });
      }
    } catch (err) {
      Swal.fire({ icon: "error", text: handleServerValidations(err), ...SWAL_SETTINGS });
    }
  };

  const ChangeStatus = (id, newStatus) => {
    const formData = new FormData();
    formData.append('status', newStatus);
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes"),
      cancelButtonText: t("btn_cancel")
    })
      .then((result) => {
        if (result.isConfirmed) {
          if (gridRef.current?.api) {
            const rowNode = gridRef.current.api.getRowNode(String(id));
            if (rowNode) {
              rowNode.setDataValue("status", newStatus);
            }
          }

          players.Status(formData, id)
            .then((response) => {
              if (response.success) {
                Swal.fire({
                  icon: "success",
                  text: response.message,
                  ...SWAL_SETTINGS,
                });
              } else {
                if (gridRef.current?.api) {
                  const rowNode = gridRef.current.api.getRowNode(String(id));
                  if (rowNode) {
                    rowNode.setDataValue(
                      "status",
                      newStatus === STATUS.ACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE
                    );
                  }
                }
                Swal.fire({
                  icon: "error",
                  text: handleServerValidations(response),
                  ...SWAL_SETTINGS,
                })
              }
            })
            .catch((error) => {
              if (gridRef.current?.api) {
                const rowNode = gridRef.current.api.getRowNode(String(id));
                if (rowNode) {
                  rowNode.setDataValue(
                    "status",
                    newStatus === STATUS.ACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE
                  )
                }
              }
              Swal.fire({
                icon: "error",
                text: handleServerValidations(error),
                ...SWAL_SETTINGS,
              });
            })
        }
      })
  };

  // 🔥 FIXED DELETE FUNCTION - S.No Issue Solved (Removed duplicate)
  function Deletefunction(data) {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      text: t("you_not_be_able_to_revert_this"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes_delete"),
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        players.Delete(data.originalId).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            // ✅ FIXED: Use refetch to recalculate S.No
            refetch();
            setSelectedRows([]);
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  }

  const handleBulkDelete = () => {
    if (selectedRows.length === 0) return;
    
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      text: `You are about to delete ${selectedRows.length} selected player(s). You will not be able to revert this!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes_delete"),
      cancelButtonText: t("btn_cancel")
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const ids = selectedRows.map(row => row.originalId);
          const response = await players.BulkDelete({ ids });
          
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message || "Records deleted successfully",
              ...SWAL_SETTINGS,
            });
            refetch();
            setSelectedRows([]);
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        } catch (error) {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        }
      }
    });
  };


  const renderProfileStatus = (status) => {
    switch (status) {
      case "verified":
        return <span className="badge badge-pill badge-success-light fw-bold text-uppercase">{t("label_verified")}</span>;
      case "rejected":
        return <span className="badge badge-pill badge-danger-light fw-bold text-uppercase">{t("label_rejected")}</span>;
      default:
        return <span className="badge badge-pill badge-warning-light fw-bold text-uppercase">{t("label_pending")}</span>;
    }
  };

  // ================= COLUMNS =================
  const colDefs = useMemo(() => [
    {
      headerName: "",
      field: "checkbox",
      maxWidth: 50,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      sortable: false,
      filter: false,
      suppressMovable: true,
      lockPosition: true,
    },
    {
      headerName: t("list_heading_sno"),
      field: "srNo",
      maxWidth: 85,
      sortable: false,
      filter: false,
      suppressMovable: true,
      lockPosition: true,
      valueGetter: (params) => {
        if (!params.node || params.node.rowIndex === undefined) return "";
        return params.node.rowIndex + 1;
      },
      cellStyle: { borderRight: "1px solid #dee2e6", color: "#3c4858" },
    },
    {
      headerName: t("label_name"),
      field: "name",
      flex: 1,
      minWidth: 200,
      sortable: true,
      filter: true,
      cellClass: 'ag-cell-ellipsis', // ✅ Added for ellipsis
    },

    {
      headerName: t("username") || "Username",
      field: "username",
      flex: 1,
      minWidth: 150,
      sortable: true,
      filter: true,
      cellRenderer: (params) => {
        return (
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value || "N/A"}
          </div>
        );
      },
    },
    {
      headerName: t("email"),
      field: "email",
      flex: 1,
      minWidth: 150,
      tooltipField: "email",
      cellRenderer: (params) => {
        return (
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value || "N/A"}
          </div>
        );
      },
    },
    {
      headerName: t("label_location"),
      field: "location",
      minWidth: 180,
      sortable: true,
      filter: true,
    },
    {
      headerName: t("label_verification"),
      field: "isProfileVerified",
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: true,
      cellRenderer: (params) => renderProfileStatus(params.value),
      cellStyle: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }
    },
    {
      headerName: t("list_heading_created_date"),
      field: "createdAt",
      filter: "agDateColumnFilter",
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      valueGetter: (params) =>
        params.data?.createdAt ? new Date(params.data.createdAt) : null,
      valueFormatter: (params) =>
        params.value ? formateDateWithMonth(params.value) : "N/A"
    },
    ...(hasP("players-status") ? [{
      headerName: t("list_heading_status"),
      field: "status",
      filter: true,
      maxWidth: 90,
      sortable: true,
      cellStyle: {
        display: "flex",
        justifyContent: "center"
      },
      cellRenderer: (params) => (
        <label className={"custom-switch cp" + (params.value !== STATUS.ACTIVE ? " e_none" : "")}>
          <input
            type="checkbox"
            className="custom-switch-input cp"
            checked={params.value === STATUS.ACTIVE}
            onChange={() =>
              ChangeStatus(
                params.data.originalId,
                params.value === STATUS.ACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE
              )
            }
          />
          <span className="custom-switch-indicator cp"></span>
        </label>
      ),
    }] : []),
    ...(hasP("players-view") || hasP("players-edit") || hasP("players-delete") ? [{
      headerName: t("list_heading_action"),
      pinned: "right",
      minWidth: 150,
      maxWidth: 150,
      sortable: false,
      filter: false,
      cellRenderer: (params) => {
        const row = params.data;
        return (
          <div className="btn-toolbar table-actions">
            <div className="btn-group btn-group-sm">
              {hasP("players-view") && (
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip style={{ fontWeight: 'bold' }}>View</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn view"
                    onClick={() => viewfunction(row)}
                  >
                    <i className="ri-eye-fill"></i>
                  </button>
                </Whisper>
              )}
              {hasP("players-edit") && (
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip style={{ fontWeight: 'bold' }}>Edit</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn edit"
                    onClick={() => Editfunction(row)}
                  >
                    <i className="ri-pencil-fill"></i>
                  </button>
                </Whisper>
              )}
              {hasP("players-delete") && (
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip style={{ fontWeight: 'bold' }}>Delete</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn delete"
                    onClick={() => Deletefunction(row)}
                  >
                    <i className="ri-delete-bin-6-fill"></i>
                  </button>
                </Whisper>
              )}
              <Whisper
                placement="top"
                trigger="hover"
                speaker={<Tooltip style={{ fontWeight: 'bold' }}>Export PDF</Tooltip>}
              >
                <button
                  type="button"
                  className="btn delete"
                  style={{ color: '#e17055' }}
                  onClick={() => downloadPDF(row)}
                >
                  <i className="ri-file-pdf-2-fill"></i>
                </button>
              </Whisper>
            </div>
          </div>
        );
      },
    }] : []),
  ], [t, navigate]);


  const rowData = useMemo(() => {
    if (isLoading || !data?.data?.list) return null;

    let filteredList = data.data.list;
    if (searchData) {
      const search = searchData.toLowerCase();
      filteredList = filteredList.filter(row =>
        (row?.firstName && row.firstName.toLowerCase().includes(search)) ||
        (row?.lastName && row.lastName.toLowerCase().includes(search)) ||
        (row?.email && row.email.toLowerCase().includes(search)) ||
        (row?.username && row.username.toLowerCase().includes(search))
      );
    }

    return filteredList.map((row, i) => ({
      srNo: 0,
      originalId: row.id,
      name: `${row?.firstName ? TrimText(row.firstName, 15) : ""} ${row?.lastName ? TrimText(row.lastName, 15) : ""}`.trim() || "N/A",
      username: row?.username || "N/A",
      email: row?.email || "N/A",
      location:
        row?.city || row?.country
          ? `${row?.city ? TrimText(row.city, 20) : ""}${row?.city && row?.country ? ", " : ""}${row?.country ? TrimText(row.country, 20) : ""}`
          : "N/A",
      isProfileVerified: row?.isProfileVerified || "pending",
      status: row.status,
      createdAt: row.createdAt,
    }));
  }, [data, searchData, isLoading]);

  // ================= AG GRID CONFIG =================
  const getAgGridConfig = useMemo(() => ({
    ref: gridRef,
    domLayout: "autoHeight",
    rowData: rowData,
    columnDefs: colDefs,
    rowSelection: "multiple",
    onSelectionChanged: (event) => setSelectedRows(event.api.getSelectedRows()),
    getRowId: (params) => String(params.data.originalId),
    noRowsOverlayComponent: NoRowsOverlay,
    pagination: true,
    paginationPageSize: perPage,
    paginationPageSizeSelector: [10, 20, 50, 100],
    suppressCellFocus: true,
    defaultColDef: {
      resizable: true,
      sortable: true,
      minWidth: 150,
      cellStyle: {
        padding: "10px 8px",
      },
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
    },
    headerHeight: 50,
    rowHeight: 45,
    onPaginationChanged: (params) => {
      if (params.api) {
        const currentPage = params.api.paginationGetCurrentPage();
        const pageSize = params.api.paginationGetPageSize();
        setPerPage(pageSize);
      }
    },
  }), [rowData, colDefs, perPage]);

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card overflow-hidden">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h6 className="main-content-label">Players</h6>
              <div className="d-flex align-items-center">
                {selectedRows.length > 0 && (
                  <button 
                    className="btn btn-danger me-3"
                    onClick={handleBulkDelete}
                  >
                    <i className="ri-delete-bin-line me-1"></i> Delete Selected ({selectedRows.length})
                  </button>
                )}
                <div style={{ width: 300 }} className="me-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("input_placeholder_search")}
                    value={searchData}
                    onChange={(e) => setSearchData(e.target.value)}
                    style={{ outline: "none", boxShadow: "none" }}
                  />
                </div>

                {/* Club filter */}
                <select
                  className="form-select form-control me-3 cp"
                  style={{ width: 140 }}
                  value={selectedClub}
                  onChange={(e) => setSelectedClub(e.target.value)}
                >
                  <option value="">{t("sidebar_link_club")}</option>
                  {clubs.map((club) => (
                    <option key={club.id} value={club.id}>
                      {club.clubDetails?.name || club.firstName}
                    </option>
                  ))}
                </select>

                {/* Verification status */}
                <select
                  className="form-select form-control me-3 cp"
                  style={{ width: 120 }}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (gridRef?.current && gridRef?.current?.api) {
                      if (value || value?.length) {
                        gridRef?.current?.api?.setFilterModel({
                          isProfileVerified: {
                            type: "equals",
                            filter: value
                          }
                        })
                      } else {
                        gridRef?.current?.api?.setFilterModel(null);
                      }
                      gridRef?.current?.api?.onFilterChanged();
                    }
                  }}
                >
                  <option value="">{t("label_verification")}</option>
                  <option value={VERIFICATION_STATUS.VERIFIED}>{t("label_verified")}</option>
                  <option value={VERIFICATION_STATUS.PENDING}>{t("label_pending")}</option>
                  <option value={VERIFICATION_STATUS.REJECTED}>{t("label_rejected")}</option>
                </select>

                {/* status */}
                <select
                  className="form-select form-control me-3 cp"
                  style={{ width: 100 }}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (gridRef && gridRef?.current && gridRef?.current?.api) {
                      if (value && value?.length) {
                        gridRef?.current?.api?.setFilterModel({
                          status: {
                            type: "equals",
                            filter: value
                          }
                        })
                      } else {
                        gridRef?.current?.api?.setFilterModel(null);
                      }
                      gridRef?.current?.api?.onFilterChanged();
                    }
                  }}
                >
                  <option value="">{t("list_heading_status")}</option>
                  <option value={STATUS.ACTIVE}>{t("btn_active")}</option>
                  <option value={STATUS.INACTIVE}>{t("btn_inactive")}</option>
                </select>

                {/* date */}
                <div>
                  <CustomProvider>
                    <DateRangePicker
                      style={{ width: "150px" }}
                      placeholder={t("input_placeholder_date")}
                      placement="bottomEnd"
                      onChange={(value) => {
                        if (!gridRef || !gridRef?.current || !gridRef?.current?.api) return;

                        if (!value || value?.length !== 2) {
                          gridRef?.current?.api?.setFilterModel(null);
                          gridRef?.current?.api?.onFilterChanged();
                          return;
                        }
                        const [start, end] = value;
                        gridRef?.current?.api?.setFilterModel({
                          createdAt: {
                            type: "inRange",
                            dateFrom: moment(start).startOf("day").format("YYYY-MM-DD HH:mm:ss"),
                            dateTo: moment(end).endOf("day").format("YYYY-MM-DD HH:mm:ss")
                          }
                        });
                        gridRef?.current?.api?.onFilterChanged();
                      }}
                    />
                  </CustomProvider>
                </div>
    {/*
                {hasP("cms-add") && (
                  <button
                    className="btn ripple btn-main-primary signbtn ml-3"
                    onClick={() => navigate(`/admin/user-management/players/add`)}
                  >
                    {t("btn_add_new")}
                  </button>
                )}
                   */}
              </div>
            </div>

            {isLoading ? (
              <Placeholder.Paragraph
                className="w-100 admin-placeholder"
                rowHeight={30}
                rowSpacing={5}
                active={true}
                rows={7}
              />
            ) : (
              <div className="ag-theme-alpine" style={{ width: '100%' }}>
                <AgGridReact {...getAgGridConfig} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PlayersList;
